import React, { useCallback, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Box, BoxProps, Typography } from '@mui/material';
import { Icon, IProduct, MarketplaceProduct } from '@alycecom/ui';

import useResponsive from '../../../../hooks/useResponsive';
import StyledSubmitButton from '../../../../components/Shared/StyledSubmitButton';
import Avatar from '../../../../components/Avatar/Avatar';
import { getSenderState } from '../../../../store/users/users.selectors';
import { getCountryById } from '../../../../store/countries/countries.selectors';

import { useStyles } from './productItem.styles';

export interface IProductItemProps {
  product: IProduct;
  isSentProduct?: boolean;
  onAccept: (product: IProduct) => void;
  onViewDetails: (product: IProduct) => void;
  wrapperProps?: BoxProps;
}

const ProductItem = ({
  product,
  isSentProduct = false,
  onAccept,
  onViewDetails,
  wrapperProps = {},
}: IProductItemProps): JSX.Element => {
  const { isMobile } = useResponsive();
  const classes = useStyles({ isMobile, isSentProduct });

  const sender = useSelector(getSenderState);
  const { image: flag } = useSelector(useMemo(() => getCountryById(product.countryId), [product])) || {};

  const handleAccept = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      onAccept(product);
    },
    [onAccept, product],
  );

  const handleViewDetails = useCallback(() => onViewDetails(product), [onViewDetails, product]);

  return (
    <MarketplaceProduct
      product={product}
      onViewDetails={handleViewDetails}
      wrapperProps={wrapperProps}
      flag={flag}
      overrideClasses={{
        media: classes.media,
        itemText: classes.itemText,
        productName: classes.productName,
      }}
    >
      <Box
        className={classNames('pt1-ie', classes.buttonBlock, {
          [classes.alwaysVisibleButtonBlock]: isSentProduct,
        })}
        position="absolute"
        width={1}
        bottom={0}
        display="flex"
        flexDirection={['column', 'row']}
        alignItems="center"
        justifyContent={['center', 'flex-start']}
        px={2}
      >
        {isSentProduct && <Avatar className={classes.avatar} src={sender?.image ?? ''} alt={sender?.firstName} />}
        <Box width={1} mx={1}>
          {isSentProduct && (
            <Box mb={1} textAlign="center">
              <Typography className="Body-Regular-Left-White">{sender?.firstName}’s gift to you</Typography>
            </Box>
          )}
          <StyledSubmitButton width="100%" height={28} onClick={handleAccept} overrideClass="submitButton-small-ie">
            <Icon className={classes.buttonIcon} icon="gift" />
            <Box className={classNames(classes.buttonText, 'productAcceptButtonText-ie')}>Accept</Box>
          </StyledSubmitButton>
        </Box>
      </Box>
    </MarketplaceProduct>
  );
};

export default memo(ProductItem);
